import { z } from 'zod';
import { AttemptCodeSchema, jsonByteLength } from './attempt.ts';
import { UsageLicenseClassSchema } from './usage.ts';

/**
 * Study progress, one row per user and license class (F6 AC-2.3, D-4): `GET /api/progress`,
 * `GET /api/progress/:licenseClass`, `PUT /api/progress/:licenseClass`.
 *
 * Sync protocol (optimistic concurrency, the merge itself is T-2.4's `ProgressRepo` sync adapter):
 *  1. `GET /api/progress/:licenseClass` → `{ data, updatedAt }` (404 when the user has no row yet).
 *  2. Merge local and server `data` (union of `done`, a question's newer `at` wins, `wrong` keeps
 *     every question still wrong; the tombstones `resetAt` / `wrongClearedAt` take the larger value
 *     and drop the answers they cover; `bookmarks`: per question the mark with the newer `at` wins,
 *     a tie keeps it on, and `resetAt` drops the marks made before it), then `PUT` with
 *     `baseUpdatedAt` = the `updatedAt` read in step 1, or without `baseUpdatedAt` when step 1 was a
 *     404.
 *  3. 200 → the stored row; keep its `updatedAt` as the next base. 409 `progress_conflict` → another
 *     device wrote in between (server `updatedAt` is newer than the base, or a row appeared when none
 *     was expected): go back to step 1. Nothing is overwritten blindly.
 *
 * `updatedAt` of the row is the server's clock (ISO 8601, strictly increasing per row); the
 * `updatedAt` / `at` numbers inside `data` are the client's clock (epoch ms), used only by the merge.
 */

/** `progress.data` cap (same as the DB CHECK, apps/api `PROGRESS_DATA_MAX_BYTES`). */
export const PROGRESS_DATA_MAX_BYTES = 16384;

/** Current version of `ProgressData`; a new shape bumps it (the server keeps accepting the old). */
export const PROGRESS_DATA_VERSION = 1;

const EpochMsSchema = z.int().min(0).max(8_640_000_000_000_000);

export const ProgressEntrySchema = z.strictObject({
  /** Last answer to the question was right. */
  ok: z.boolean(),
  /** When it was last answered (client epoch ms). */
  at: EpochMsSchema,
});
export type ProgressEntry = z.infer<typeof ProgressEntrySchema>;

/**
 * Added in F8 T-3.5: the learner's "Đánh dấu câu" mark of one question. Removing a mark keeps it as
 * `on: false` (a tombstone), so another device that still has it on does not bring it back.
 */
export const ProgressBookmarkSchema = z.strictObject({
  /** Marked (`true`) or unmarked (`false`, the tombstone). */
  on: z.boolean(),
  /** When it was last set or cleared (client epoch ms). */
  at: EpochMsSchema,
});
export type ProgressBookmark = z.infer<typeof ProgressBookmarkSchema>;

/** Versioned progress JSON of one license class, ≤ 16 KB. Keys are stable question ids. */
export const ProgressDataSchema = z
  .strictObject({
    v: z.literal(PROGRESS_DATA_VERSION),
    /** Questions answered at least once. */
    done: z.record(AttemptCodeSchema, ProgressEntrySchema),
    /** Questions to review again (answered wrongly and not yet cleared). */
    wrong: z.array(AttemptCodeSchema).max(1000),
    /** Last local change (client epoch ms). */
    updatedAt: EpochMsSchema,
    /**
     * Added in F6 T-2.4 (optional, still `v: 1`): last "start over" of the class (client epoch ms).
     * Answers with `at` before it are dropped by every merge, so a reset is not undone by a device
     * that still holds the old answers.
     */
    resetAt: EpochMsSchema.optional(),
    /**
     * Added in F6 T-2.4 (optional, still `v: 1`): last "clear the wrong list" (client epoch ms).
     * `wrong` entries answered at or before it are dropped by every merge (`done` is kept).
     */
    wrongClearedAt: EpochMsSchema.optional(),
    /**
     * Added in F8 T-3.5 (optional, still `v: 1`; payloads without it stay valid): bookmark marks by
     * question id, tombstones included. Counted in the same 16 KB as the rest of the row.
     */
    bookmarks: z.record(AttemptCodeSchema, ProgressBookmarkSchema).optional(),
  })
  .refine((d) => jsonByteLength(d) <= PROGRESS_DATA_MAX_BYTES, {
    message: `data must be at most ${PROGRESS_DATA_MAX_BYTES} bytes of JSON`,
  });
export type ProgressData = z.infer<typeof ProgressDataSchema>;

/** `:licenseClass` of the progress routes (`B`, `C1` ...). */
export const ProgressLicenseClassSchema = UsageLicenseClassSchema;

/**
 * `PUT /api/progress/:licenseClass` body (the class is the path). `baseUpdatedAt`: the row's
 * `updatedAt` this write is based on; absent = "I saw no row" (create only). 409 when it is stale.
 */
export const ProgressPutSchema = z.strictObject({
  data: ProgressDataSchema,
  baseUpdatedAt: z.iso.datetime().optional(),
});
export type ProgressPut = z.infer<typeof ProgressPutSchema>;

/** One stored progress row. */
export const ProgressOutSchema = z.strictObject({
  licenseClass: z.string().min(1),
  data: ProgressDataSchema,
  /** Server time of the last write (ISO 8601 UTC): the next `baseUpdatedAt`. */
  updatedAt: z.iso.datetime(),
});
export type ProgressOut = z.infer<typeof ProgressOutSchema>;

/** `GET /api/progress`: every class of the user, by class. */
export const ProgressListSchema = z.strictObject({ items: z.array(ProgressOutSchema) });
export type ProgressList = z.infer<typeof ProgressListSchema>;
